import dayjs from "dayjs"

export const RESERVATION_PENDING_REFRESH_EVENT = "reservation-pending-refresh"

export type ReservationRealtimeStatus =
  | "pending"
  | "confirmed"
  | "arrived"
  | "completed"
  | "rejected"
  | "cancelled"
  | "expired"

export interface ReservationStatusChangedEvent {
  eventId: string
  occurredAt: string
  shopId: number
  source: "ONLINE"
  sourceId: number
  status: ReservationRealtimeStatus
  version: number
}

const RESERVATION_REALTIME_STATUSES = new Set<ReservationRealtimeStatus>([
  "pending",
  "confirmed",
  "arrived",
  "completed",
  "rejected",
  "cancelled",
  "expired",
])

function isPositiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

function parseObject(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : undefined
    }
    catch {
      return undefined
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined
}

export function parseReservationStatusChangedMessage(raw: unknown): ReservationStatusChangedEvent | undefined {
  const envelope = parseObject(raw)
  if (envelope?.type !== "reservation_status_changed") return undefined
  const payload = parseObject(envelope.data)
  if (!payload) return undefined

  const eventId = typeof payload.eventId === "string" ? payload.eventId.trim() : ""
  const occurredAt = typeof payload.occurredAt === "string" ? payload.occurredAt.trim() : ""
  const status = payload.status as ReservationRealtimeStatus
  if (
    !eventId
    || !occurredAt
    || payload.source !== "ONLINE"
    || !isPositiveInteger(payload.shopId)
    || !isPositiveInteger(payload.sourceId)
    || !Number.isInteger(payload.version)
    || Number(payload.version) < 0
    || !RESERVATION_REALTIME_STATUSES.has(status)
  ) return undefined

  return {
    eventId,
    occurredAt,
    shopId: payload.shopId,
    source: "ONLINE",
    sourceId: payload.sourceId,
    status,
    version: payload.version as number,
  }
}

export function createReservationPendingEventDeduper(limit = 200) {
  const maximum = Math.max(1, Math.trunc(limit))
  const orderedIds: string[] = []
  const rememberedIds = new Set<string>()

  return {
    remember(eventId: string) {
      if (rememberedIds.has(eventId)) return false
      rememberedIds.add(eventId)
      orderedIds.push(eventId)
      while (orderedIds.length > maximum) {
        const expired = orderedIds.shift()
        if (expired) rememberedIds.delete(expired)
      }
      return true
    },
    size: () => rememberedIds.size,
  }
}

export function shouldAlertReservationPendingEvent(
  event: ReservationStatusChangedEvent,
  context: { canView: boolean, eventIsNew: boolean, shopId?: number },
) {
  return context.canView
    && context.eventIsNew
    && context.shopId === event.shopId
    && event.source === "ONLINE"
    && event.status === "pending"
}

function normalizePendingDate(value: string | null | undefined) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined
  const parsed = dayjs(value)
  return parsed.isValid() && parsed.format("YYYY-MM-DD") === value ? value : undefined
}

export function buildReservationPendingRoute(earliestPendingDate?: string | null) {
  const pendingDate = normalizePendingDate(earliestPendingDate)
  return {
    name: "ReservationWorkbench",
    query: {
      ...(pendingDate ? { smartReservationDate: pendingDate } : {}),
      smartReservationPanel: "online",
    },
  }
}

export function requestReservationPendingRefresh() {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(RESERVATION_PENDING_REFRESH_EVENT))
}
